import QuoteItem from "./QuoteItem";
import Amount from "../common/Amount";
import {Option} from "ts-option";
import {CreateQuoteDto} from "../../api/model/rest";
import {QuoteCalculator} from "../../utils/QuoteCalculator";

export interface QuotePrices {
	labourTotalCost: Amount
	totalPrice: Amount
	vatAmount: Option<Amount>
	markupAmount: Amount
	totalBeforeVat: Amount,
	hiberCommission: Amount,
	installerGross: Amount,
}

export const enum QuotePartsPriceStrategy {
	BEST_AVAILABILITY = "BEST_AVAILABILITY",
	LOWER_PRICE = "LOWER_PRICE",
}

/**
 * Quote object
 */
export default class Quote {

	constructor(public readonly id: string,
		public readonly appointmentId: Option<string>,
		public readonly leadId: Option<string>,
		public readonly brandId: string,
		public readonly partnerBrandId: Option<string>,
		public readonly vat: number,
		public readonly items: QuoteItem[],
		public readonly summary: string,
		public readonly description: Option<string>,
		public readonly labourTotalCost: Amount,
		public readonly totalPrice: Amount,
		public readonly totalPriceBeforeRoundUp: Amount,
		public readonly totalPriceBeforeVat: Amount,
		public readonly vatAmount: Option<Amount>,
		public readonly markup: number,
		public readonly markupAmount: Amount,
		public readonly vatRegistered: boolean,
		public readonly hiberCommission: Option<Amount>,
		public readonly installerGross: Option<Amount>,
		public readonly partsPriceStrategy: string,
		public readonly maxFee: number) {
	}

	toJsonCreateDto(): CreateQuoteDto {
		return {
			appointmentId: this.appointmentId.orNull,
			leadId: this.leadId.orNull,
			brandId: this.brandId,
			partnerBrandId: this.partnerBrandId.orNull,
			items: this.items.map(i => i.toJsonCreateItemDto()),
			summary: this.summary,
			description: this.description.orNull,
			labourTotalCost: this.labourTotalCost.cents,
			totalPrice: this.totalPrice.cents,
			totalPriceBeforeRoundUp: this.totalPriceBeforeRoundUp.cents, // totalPriceBeforeRoundUp - copy of totalPrice
			totalPriceBeforeVat: this.totalPriceBeforeVat.cents,
			vatAmount: this.vatAmount.map(v => v.cents).orNull,
			markup: this.markup,
			markupAmount: this.markupAmount.cents,
			vatRegistered: this.vatRegistered,
			hiberCommission: this.hiberCommission.map(a => a.cents).orNull,
			installerGross: this.installerGross.map(a => a.cents).orNull,
			partsPriceStrategy: this.partsPriceStrategy,
			maxFee: this.maxFee
		}
	}

	static calculatePrices(vat: number,
		markup: number,
		items: QuoteItem[],
		labourQuantity: number,
		vatRegistered: boolean,
		labourPrice: number,
		maxFee: number): QuotePrices {
		const labourTotalCost = new Amount(labourPrice).multiple(labourQuantity);

		const calcData = QuoteCalculator.calculateFromLabourCost(labourTotalCost, {
			markup,
			vat,
			vatRegistered,
			items,
			maxFee
		});

		return {
			labourTotalCost,
			totalPrice: calcData.totalPrice,
			vatAmount: calcData.totalVat,
			markupAmount: calcData.totalMargin,
			totalBeforeVat: calcData.totalPriceBeforeVat,
			hiberCommission: calcData.hiberCommission,
			installerGross: calcData.installerGross
		}
	}

	copy({
		id = this.id,
		appointmentId = this.appointmentId,
		leadId = this.leadId,
		brandId = this.brandId,
		vat = this.vat,
		items = this.items,
		summary = this.summary,
		description = this.description,
		labourTotalCost = this.labourTotalCost,
		totalPrice = this.totalPrice,
		totalPriceBeforeRoundUp = this.totalPriceBeforeRoundUp,
		totalPriceBeforeVat = this.totalPriceBeforeVat,
		vatAmount = this.vatAmount,
		markup = this.markup,
		markupAmount = this.markupAmount,
		vatRegistered = this.vatRegistered,
		hiberCommission = this.hiberCommission,
		installerGross = this.installerGross,
		partsPriceStrategy = this.partsPriceStrategy,
	}) {
		return new Quote(id,
			appointmentId,
			leadId,
			brandId,
			this.partnerBrandId,
			vat,
			items,
			summary,
			description,
			labourTotalCost,
			totalPrice,
			totalPriceBeforeRoundUp,
			totalPriceBeforeVat,
			vatAmount,
			markup,
			markupAmount,
			vatRegistered,
			hiberCommission,
			installerGross,
			partsPriceStrategy, this.maxFee)
	}

	static calculatePartsMarkup(items: QuoteItem[], vatRegistered: boolean) {
		let sumPrice = new Amount(0);
		items.forEach(i => sumPrice = sumPrice.add(vatRegistered ? i.markupAmount : i.markupAmountWithVat));
		return sumPrice;
	}

	/**
	 * Calculate total price
	 * @param {QuoteItem[]} items
	 * @returns price of the items
	 */
	static calculateQuoteItemTotalCost(items: QuoteItem[]): Amount {
		let sumPrice = new Amount(0);
		items.forEach(i => sumPrice = sumPrice.add(i.getTotalPrice()));
		return sumPrice;
	}
}
